import React, { ReactNode } from "react";
import styled, { css } from "styled-components";
import { ArrowUpDown, ChevronRight } from "lucide-react";
import Dropdown from "common/Dropdown/Dropdown";
import { Link } from "react-router-dom";
import { hexToRGB } from "@shared/utils/misc";
import {
  getNewLookTokens,
  getPanelShadow,
  newLookPhoneType,
} from "common/NewLook/tokens.ts";
import {
  NEW_LOOK_PHONE_CARD_GAP,
  NEW_LOOK_PHONE_CONTROL,
  NEW_LOOK_PHONE_GUTTER,
  NEW_LOOK_PHONE_HIT,
} from "common/NewLook/layout.ts";
import { PHONE_META_TAIL_ID } from "testIds";

/** The phone dashboard's soft card shadow (a hairline in dark mode). */
export const phoneCardShadow = css`
  box-shadow: ${(p) => getPanelShadow(p.theme)};
`;

export const PhoneMetaPair = ({
  lead,
  tail,
  compactTail,
}: {
  lead: string;
  tail?: string;
  compactTail?: string;
}) => (
  <MetaPair>
    <MetaLead title={lead}>{lead}</MetaLead>
    {tail && (
      <MetaTail data-testid={PHONE_META_TAIL_ID} $hasCompact={!!compactTail}>
        {` · ${tail}`}
      </MetaTail>
    )}
    {tail && compactTail && <CompactTail>{` · ${compactTail}`}</CompactTail>}
  </MetaPair>
);

/** One white card: rows separated by hairlines. */
export const PhoneCard = styled.div`
  margin: 0 16px;
  padding: 6px 14px;
  border-radius: ${(p) => p.theme.radii.card}px;
  background: ${(p) => p.theme.colors.cardBgColor};
  overflow: hidden;
  ${phoneCardShadow};
`;

export const PhoneCardSlot = styled.div<{ $flush?: boolean }>`
  padding: ${(p) =>
    p.$flush ? 0 : `0 ${NEW_LOOK_PHONE_GUTTER}px ${NEW_LOOK_PHONE_CARD_GAP}px`};
`;

export const PhoneRowDivider = css`
  &:not(:last-child) {
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;

interface PhoneSectionProps {
  title: ReactNode;
  seeAllTo?: string;
  seeAllLabel?: string;
  extra?: ReactNode;
  /** Pushed to the right edge (when there is no "See all" link). */
  trailing?: ReactNode;
}

interface PhoneSortOption<T extends string> {
  key: T;
  label: string;
}

interface PhoneSortMenuProps<T extends string> {
  options: PhoneSortOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}

export const PhoneSortMenu = <T extends string>({
  options,
  value,
  onChange,
  label,
}: PhoneSortMenuProps<T>) => {
  const current = options.find((option) => option.key === value)?.label;
  return (
    <Dropdown
      title={label}
      menu={{
        items: options.map((option) => ({
          key: option.key,
          label: option.label,
          onClick: () => onChange(option.key),
        })),
        selectedKeys: [value],
      }}
      trigger={["click"]}
    >
      <PhoneControlButton
        type="button"
        aria-label={current ? `${label}: ${current}` : label}
      >
        <ArrowUpDown size={13} strokeWidth={2} />
        {current}
      </PhoneControlButton>
    </Dropdown>
  );
};

/** Section title with a "See all" link on the right. */
export const PhoneSectionHead = ({
  title,
  seeAllTo,
  seeAllLabel,
  extra,
  trailing,
}: PhoneSectionProps) => (
  <Head>
    <Title>{title}</Title>
    {extra}
    {trailing && <div style={{ marginLeft: "auto" }}>{trailing}</div>}
    {seeAllTo && (
      <Link to={seeAllTo} style={{ marginLeft: "auto" }}>
        <SeeAll>
          {seeAllLabel}
          <ChevronRight size={14} strokeWidth={2} />
        </SeeAll>
      </Link>
    )}
  </Head>
);

/** One phone section head everywhere: title (17/500) at the 16 gutter. */
const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 24px;
  padding: 22px ${NEW_LOOK_PHONE_GUTTER}px 10px;
`;
const Title = styled.h2`
  margin: 0;
  ${newLookPhoneType.section};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
/** 36px visual, 40px hit, without growing the head (negative margins). */
const phoneSectionActionStyle = css`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: ${NEW_LOOK_PHONE_CONTROL}px;
  margin: -6px -8px -6px 0;
  padding: 0 8px;
  border: 0;
  border-radius: 8px;
  background: none;
  font-family: inherit;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  color: ${(p) => getNewLookTokens(p.theme).accent};
  -webkit-tap-highlight-color: transparent;
  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: ${NEW_LOOK_PHONE_HIT}px;
    transform: translateY(-50%);
  }
  &:active {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 0;
  }
`;
const SeeAll = styled.span`
  ${phoneSectionActionStyle};
`;
/** A section head's trailing text action ("Add account", "Buy"). */
export const PhoneSectionAction = styled.button`
  ${phoneSectionActionStyle};
`;

export const PhoneSkeleton = styled.span<{
  $width: number;
  $height: number;
  $radius?: number;
}>`
  display: block;
  flex: none;
  width: ${(p) => p.$width}px;
  max-width: 100%;
  height: ${(p) => p.$height}px;
  border-radius: ${(p) => p.$radius ?? 6}px;
  background: ${(p) => p.theme.colors.cardLightBgColor};
  animation: phonePulse 1.4s ease-in-out infinite;
  @keyframes phonePulse {
    50% {
      opacity: 0.5;
    }
  }
`;

export const PhoneLoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  ${PhoneRowDivider};
`;

export const PhonePill = styled.span<{ $tone: "green" | "red" }>`
  display: inline-block;
  flex: none;
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${(p) => p.theme.colors[p.$tone]};
  background: ${(p) => {
    const hex = p.theme.colors[p.$tone];
    return p.theme.isDarkMode
      ? hexToRGB(hex, 0.16)
      : p.$tone === "green"
      ? "#E3F6EE"
      : "#FDECEC";
  }};
`;

export default React.memo(PhoneCard);

/** Phone control scale: 36px visual, a 40px hit area (::before). */
export const PhoneControlButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: ${NEW_LOOK_PHONE_CONTROL}px;
  /* Sits in the section head without growing it. */
  margin: -6px 0;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  color: ${(p) => getNewLookTokens(p.theme).accent};
  font: inherit;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: ${NEW_LOOK_PHONE_HIT}px;
    transform: translateY(-50%);
  }
`;
const MetaPair = styled.div`
  display: flex;
  min-width: 0;
  ${newLookPhoneType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const MetaLead = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const MetaTail = styled.span<{ $hasCompact: boolean }>`
  flex: none;
  white-space: pre;
  @media (max-width: 359.98px) {
    ${(p) => p.$hasCompact && "display: none;"}
  }
`;
const CompactTail = styled.span`
  display: none;
  flex: none;
  white-space: pre;
  @media (max-width: 359.98px) {
    display: inline;
  }
`;
